import React, { FC, ReactNode } from "react";
import { Tooltip } from "@/components/Tooltip/Tooltip";

import { motion } from "framer-motion";
import { v4 as uuidv4 } from "uuid";
import "./Button.scss";

// Define allowed values for iconPosition and type
type IconPosition = "start" | "end";
type ButtonType =
  | "primary"
  | "secondary"
  | "success"
  | "danger"
  | "warning"
  | "info"
  | "light"
  | "text"
  | "outline-primary"
  | "outline-secondary"
  | "media-status"
  | "media-id"
  | "text-only";

interface ButtonProps {
  icon?: ReactNode;
  iconPosition?: IconPosition;
  type?: ButtonType;
  className?: string;
  children?: ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  HTMLButtonType?: any;
  isDisabled?: boolean;
  style?: any;
  tooltipText?: string;
  title?: string;
  tooltipPlacement?: "top" | "bottom" | "left" | "right";
}

const getClassesForButtonType = (
  buttonType: ButtonType,
  isDisabled: boolean
): string => {
  switch (buttonType) {
    case "primary":
      return "btn btn-primary";
    case "light":
      return "btn btn-light";
    case "outline-primary":
      return "btn btn-outline-primary";
    case "secondary":
      return "btn btn-secondary";
    case "outline-secondary":
      return "btn btn-outline-secondary";
    case "text":
      return "btn btn-text";
    case "media-status":
      return "btn media-status";
    case "media-id":
      return "btn media-id";
    case "danger":
      return "btn btn-outline-danger";
    case "text-only":
      return `btn border-0 p-2 ${!isDisabled ? "active" : ""}`;
    default:
      return "btn btn-primary";
  }
};

export const Button: FC<ButtonProps> = ({
  icon,
  iconPosition = "start",
  children,
  className = "",
  type = "primary",
  HTMLButtonType,
  isDisabled = false,
  style,
  tooltipText,
  tooltipPlacement,
  ...props
}) => {
  const classes = getClassesForButtonType(type, isDisabled);
  const hasHoverWhite = className.includes("hover-white");

  const buttonId = uuidv4();
  const button = (
    <motion.button
      disabled={isDisabled === true}
      whileTap={{ scale: 0.95 }}
      className={`${classes} ${className}`}
      {...props}
      type={HTMLButtonType}
      style={style}
      id={buttonId}
    >
      {iconPosition === "start" && icon && (
        <i
          className={`${children ? "mx-1" : ""} ${
            hasHoverWhite ? "hover-white-icon" : ""
          }`}
        >
          {icon}
        </i>
      )}
      {children}
      {iconPosition === "end" && icon && (
        <i
          className={`${children ? "mx-1" : ""} ${
            hasHoverWhite ? "hover-white-icon" : ""
          }`}
        >
          {icon}
        </i>
      )}
    </motion.button>
  );
  return tooltipText ? (
    <Tooltip content={tooltipText} placement={tooltipPlacement}>
      {button}
    </Tooltip>
  ) : (
    button
  );
};

export default Button;
